import { useCallback, useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminConversationContentAccessRequest,
  AdminConversationQuery,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import {
  accessConversationContent,
  searchConversationHistory,
} from "./conversation-history-use-cases";
import {
  toConversationHistoryViewModel,
  toConversationTranscriptViewModel,
  type ConversationHistoryViewModel,
  type ConversationTranscriptViewModel,
} from "./conversation-history-view-model";

export type ConversationHistoryScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminConversationQuery;
};

export type ConversationHistoryScreenModel = AdminScreenState<ConversationHistoryViewModel>;
export type ConversationTranscriptScreenModel = AdminScreenState<ConversationTranscriptViewModel>;

const DEFAULT_QUERY: AdminConversationQuery = { limit: 100 };

function createDependenciesKey(dependencies: ConversationHistoryScreenModelDependencies): string {
  return JSON.stringify({
    permissions: dependencies.adminPermissions,
    query: dependencies.query ?? DEFAULT_QUERY,
  });
}

export async function resolveConversationHistoryScreenModel(
  dependencies: ConversationHistoryScreenModelDependencies,
): Promise<ConversationHistoryScreenModel> {
  const locale = dependencies.locale ?? "ko";
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.conversations.metadata.read")) {
    return createAdminPermissionDeniedState(
      locale,
      {
        en: "Conversation metadata read permission is missing.",
        ko: "대화 메타데이터 조회 권한이 없습니다.",
      },
      "admin_conversations_permission_denied",
    );
  }

  try {
    const query = dependencies.query ?? DEFAULT_QUERY;
    const envelope = await searchConversationHistory(dependencies.queryPort, query);
    if (envelope.data.items.length === 0) {
      return { status: "empty" };
    }

    const viewModel = toConversationHistoryViewModel(envelope.data, envelope.meta, query, locale);
    if (viewModel.freshness === "stale") {
      return { status: "stale", viewModel };
    }
    if (viewModel.freshness === "partial") {
      return { status: "partial", viewModel };
    }
    return { status: "ready", viewModel };
  } catch (error) {
    return toAdminScreenErrorState(locale, error);
  }
}

export async function resolveConversationContentAccessModel(
  dependencies: ConversationHistoryScreenModelDependencies,
  input: AdminConversationContentAccessRequest,
): Promise<ConversationTranscriptScreenModel> {
  const locale = dependencies.locale ?? "ko";
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.conversations.content.read")) {
    return createAdminPermissionDeniedState(
      locale,
      {
        en: "Only the security read-only role can access conversation content.",
        ko: "대화 원문은 보안 읽기 전용 역할만 조회할 수 있습니다.",
      },
      "admin_conversation_content_permission_denied",
    );
  }

  try {
    const envelope = await accessConversationContent(dependencies.queryPort, input);
    const viewModel = toConversationTranscriptViewModel(envelope.data, locale);
    return viewModel.messages.length === 0
      ? { status: "empty", viewModel }
      : { status: "ready", viewModel };
  } catch (error) {
    return toAdminScreenErrorState(locale, error);
  }
}

export function useConversationHistoryScreenModel(
  dependencies: ConversationHistoryScreenModelDependencies,
  refreshSignal?: number,
): ConversationHistoryScreenModel {
  const [state, setState] = useState<ConversationHistoryScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);

  useEffect(() => {
    let cancelled = false;
    setState((current) => createLoadingAdminScreenState(current.viewModel));
    void resolveConversationHistoryScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey, refreshSignal]);

  return state;
}

export function useConversationContentAccessModel(
  dependencies: ConversationHistoryScreenModelDependencies,
): {
  access: (input: AdminConversationContentAccessRequest) => Promise<void>;
  clear: () => void;
  state: ConversationTranscriptScreenModel;
} {
  const [state, setState] = useState<ConversationTranscriptScreenModel>(createIdleAdminScreenState());

  const clear = useCallback(() => setState(createIdleAdminScreenState()), []);
  const access = useCallback(async (input: AdminConversationContentAccessRequest) => {
    setState(createLoadingAdminScreenState());
    setState(await resolveConversationContentAccessModel(dependencies, input));
  }, [dependencies]);

  return { access, clear, state };
}
